import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Phone, MapPin, ArrowRight, Star, ChevronRight, LogIn, FileText, Menu, X } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const Reveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}>
      {children}
    </motion.div>
  );
};

const SharedNav = () => {
  const { lang, switchLang } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { to: '/', label: lang === 'es' ? 'INICIO' : 'HOME' },
    { to: '/about', label: lang === 'es' ? 'NOSOTROS' : 'ABOUT' },
    { to: '/services', label: lang === 'es' ? 'SERVICIOS' : 'SERVICES' },
    { to: '/service-areas', label: lang === 'es' ? 'AREAS' : 'AREAS' },
    { to: '/faq', label: 'FAQ' },
    { to: '/contact', label: lang === 'es' ? 'CONTACTO' : 'CONTACT' },
    { to: '/intake', label: lang === 'es' ? 'FORMULARIOS' : 'FORMS', icon: <FileText size={14} />, highlight: true },
  ];

  return (
    <>
      <div className="emergency-banner">
        <span className="emergency-banner__text">{lang === 'es' ? 'SERVICIO DE EMERGENCIA 24/7' : '24/7 EMERGENCY BAIL SERVICE'}</span>
        <a href="tel:7132288800" className="emergency-banner__phone">(713) 228-8800</a>
        <div className="lang-toggle">
          <button className={`lang-toggle__btn ${lang === 'en' ? 'lang-toggle__btn--active' : ''}`} onClick={() => switchLang('en')}>EN</button>
          <button className={`lang-toggle__btn ${lang === 'es' ? 'lang-toggle__btn--active' : ''}`} onClick={() => switchLang('es')}>ES</button>
        </div>
      </div>
      <nav className="cinematic-nav cinematic-nav--scrolled">
        <div className="nav-inner">
          <Link to="/" className="nav-logo">
            <img src="https://customer-assets.emergentagent.com/job_bond-app-hub/artifacts/95cp39mf_LONESTAR_BONDINGLOGO2.jpg"
              alt="Lone Star Bonding Co." className="nav-logo__img" />
            <div className="nav-logo__text">
              <span className="nav-logo__name">LONE STAR BONDING</span>
              <span className="nav-logo__license">LICENSE #74629</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="nav-links">
            {navLinks.map(link => (
              <Link key={link.to} to={link.to}
                className={`nav-link ${link.highlight ? 'nav-link--forms' : ''}`}
                data-testid={`nav-${link.label.toLowerCase()}`}>
                {link.icon && link.icon} {link.label}
              </Link>
            ))}
            <Link to="/dashboard" className="nav-link nav-link--signin" data-testid="nav-signin">
              <LogIn size={15} /> {lang === 'es' ? 'ENTRAR' : 'SIGN IN'}
            </Link>
            <a href="tel:7132288800" className="btn-emergency" style={{ padding: '0.6rem 1.5rem', fontSize: '1rem' }}>
              <Phone size={18} /> {lang === 'es' ? 'LLAMAR' : 'CALL NOW'}
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button className="nav-mobile-toggle" onClick={() => setMobileOpen(!mobileOpen)}
            style={{ display: 'none', background: 'none', border: '1px solid var(--border-dim)', color: 'white', padding: '0.4rem', cursor: 'pointer' }}
            data-testid="mobile-menu-toggle">
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="nav-mobile-menu" data-testid="mobile-menu">
            {navLinks.map(link => (
              <Link key={link.to} to={link.to} className="nav-mobile-menu__link" onClick={() => setMobileOpen(false)}>
                {link.icon && link.icon} {link.label}
              </Link>
            ))}
            <Link to="/dashboard" className="nav-mobile-menu__link nav-mobile-menu__link--signin" onClick={() => setMobileOpen(false)}>
              <LogIn size={16} /> {lang === 'es' ? 'ENTRAR AL PORTAL' : 'SIGN IN TO PORTAL'}
            </Link>
            <a href="tel:7132288800" className="btn-emergency" style={{ width: '100%', textAlign: 'center', justifyContent: 'center', marginTop: '0.5rem' }}>
              <Phone size={18} /> {lang === 'es' ? 'LLAMAR AHORA' : 'CALL NOW'}
            </a>
          </div>
        )}
      </nav>
    </>
  );
};

const SharedFooter = () => {
  const { lang } = useLanguage();
  return (
    <footer className="cinematic-footer">
      <div className="footer-inner">
        <div>
          <img src="https://customer-assets.emergentagent.com/job_bond-app-hub/artifacts/jwllno7h_LINESTARBONDING-LOGO1.jpg" alt="Lone Star Bonding Co." className="footer-brand__logo" />
          <p className="footer-brand__desc">{lang === 'es' ? 'Sirviendo a Houston y el Condado de Harris con servicios de fianzas confiables por mas de 30 anos. Licencia #74629.' : 'Serving Houston and Harris County with trusted bail bond services for over 30 years. License #74629.'}</p>
        </div>
        <div>
          <h4 className="footer-col__title">{lang === 'es' ? 'NAVEGACION' : 'NAVIGATION'}</h4>
          <Link to="/" className="footer-col__link">{lang === 'es' ? 'Inicio' : 'Home'}</Link>
          <Link to="/about" className="footer-col__link">{lang === 'es' ? 'Nosotros' : 'About Us'}</Link>
          <Link to="/services" className="footer-col__link">{lang === 'es' ? 'Servicios' : 'Services'}</Link>
          <Link to="/how-bail-works" className="footer-col__link">{lang === 'es' ? 'Como Funciona' : 'How Bail Works'}</Link>
          <Link to="/faq" className="footer-col__link">FAQ</Link>
        </div>
        <div>
          <h4 className="footer-col__title">{lang === 'es' ? 'RECURSOS' : 'RESOURCES'}</h4>
          <Link to="/intake" className="footer-col__link">{lang === 'es' ? 'Formulario de Solicitud' : 'Application Form'}</Link>
          <Link to="/dashboard" className="footer-col__link">{lang === 'es' ? 'Portal del Personal' : 'Staff Portal (Sign In)'}</Link>
          <Link to="/service-areas" className="footer-col__link">{lang === 'es' ? 'Areas de Servicio' : 'Service Areas'}</Link>
          <Link to="/contact" className="footer-col__link">{lang === 'es' ? 'Contacto' : 'Contact Us'}</Link>
        </div>
        <div>
          <h4 className="footer-col__title">{lang === 'es' ? 'CONTACTO' : 'CONTACT'}</h4>
          <a href="tel:7132288800" className="footer-col__link" style={{ color: 'var(--astros-orange)', fontWeight: 700 }}>(713) 228-8800</a>
          <span className="footer-col__link">1420 Washington Ave</span>
          <span className="footer-col__link">Houston, TX 77002</span>
          <span className="footer-col__link" style={{ color: 'var(--astros-orange)', fontWeight: 600, marginTop: '0.5rem' }}>{lang === 'es' ? 'Disponible 24/7' : 'Available 24/7'}</span>
        </div>
      </div>
      <div className="footer-bottom">
        <p className="footer-bottom__copy">&copy; {new Date().getFullYear()} Lone Star Bonding Co. All rights reserved. License #74629.</p>
        <p className="footer-bottom__credit">Developed by James Hudson @ Texas IT Nerds | Protected by TITN Aegis Security</p>
      </div>
    </footer>
  );
};

export { SharedNav, SharedFooter, Reveal };
